'use client';

/* istanbul ignore file */
import { FC, useEffect, useState } from 'react';

// Import date-fns locales for internationalization
import { bg, cs, da, de, el, enGB, es, fi, fr, hr, hu, it, lt, mk, nl, nn, pl, pt, ro, ru, sk, sl, sr, sv, tr, uk } from 'date-fns/locale';
import DatePicker, { registerLocale } from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import './Calendar.css';

// Register all locales
registerLocale('bg', bg);
registerLocale('cs', cs);
registerLocale('da', da);
registerLocale('de', de);
registerLocale('el', el);
registerLocale('en-GB', enGB);
registerLocale('es', es);
registerLocale('fi', fi);
registerLocale('fr', fr);
registerLocale('hr', hr);
registerLocale('hu', hu);
registerLocale('it', it);
registerLocale('lt', lt);
registerLocale('mk', mk);
registerLocale('nl', nl);
registerLocale('nn', nn);
registerLocale('pl', pl);
registerLocale('pt', pt);
registerLocale('ro', ro);
registerLocale('ru', ru);
registerLocale('sk', sk);
registerLocale('sl', sl);
registerLocale('sr', sr);
registerLocale('sv', sv);
registerLocale('tr', tr);
registerLocale('uk', uk);

type Day = 'Monday' | 'Tuesday' | 'Wednesday' | 'Thursday' | 'Friday' | 'Saturday' | 'Sunday';

interface Props {
   value?: Date;
   min?: Date;
   max?: Date;
   disabledDates?: Array<Date>;
   disabledDays?: Array<Day>;
   onChange?: (date: Date) => void;
   locale?: string;
}

const DAY_MAP: Record<Day, number> = {
   Sunday: 0,
   Monday: 1,
   Tuesday: 2,
   Wednesday: 3,
   Thursday: 4,
   Friday: 5,
   Saturday: 6
};

export const Calendar: FC<Props> = ({ value, min = new Date(1900, 0, 1), max = new Date(2099, 11, 31), disabledDates = [], disabledDays = [], onChange, locale = 'en-GB' }) => {
   const [selectedDate, setSelectedDate] = useState<Date | undefined>(value);

   const isDateDisabled = (date: Date): boolean => {
      if (date < min || date > max) return true;

      // Check if day of week is disabled
      const dayOfWeek = date.getDay();
      const isDisabledDay = disabledDays.some((day) => DAY_MAP[day] === dayOfWeek);

      // Check if specific date is disabled
      const isDisabledDate = disabledDates.some((disabledDate) => {
         return disabledDate.getFullYear() === date.getFullYear() && disabledDate.getMonth() === date.getMonth() && disabledDate.getDate() === date.getDate();
      });

      return isDisabledDay || isDisabledDate;
   };

   const handleChange = (date: Date | null) => {
      if (!date) return;
      if (isDateDisabled(date)) return;

      setSelectedDate(date);
      onChange?.(date);
   };

   useEffect(() => {
      setSelectedDate(value);
   }, [value]);

   return (
      <div className="calendar-wrapper">
         <DatePicker selected={selectedDate} onChange={handleChange} minDate={min} maxDate={max} filterDate={(date) => !isDateDisabled(date)} locale={locale} inline calendarStartDay={1} />
      </div>
   );
};
